import React, { useContext, useEffect, useState } from 'react'
import { ChatFeedbackModal } from './ChatFeedbackModal'
import {
  IconVolume2,
  IconThumbUp,
  IconThumbUpFilled,
  IconThumbDown,
  IconThumbDownFilled,
  IconPencil,
  IconCopy,
  IconCheck,
  IconAlignJustified,
  IconLayoutColumns,
  IconChevronLeft,
  IconChevronRight
} from '@tabler/icons-react'
import { useSelector } from 'react-redux'
import { ChatbotContext } from '../context/ChatbotProvider'
import { useChatService } from '../services/chatService'
import { navigateMsgResponse } from '../utils/TextUtils'
import { checkIsDrawerMode } from '../utils/helpers'
import { getModelDisplayName, getVoice } from '../utils/constants'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'
//@ts-ignore
import { Badge } from 'dsm-react-lib'
import { Tooltip } from 'react-tooltip'
import useSpeech from '../../../../_hooks/useSpeech'
import { toast } from 'react-toastify'
import { useThemeManager } from '../../../../_hooks/useThemeManager'
import { ChatBubbleItem } from '../utils/types'
import ChatModelDropdown from './ChatModelDropdown'

interface ChatFeedbackProps {
  showFeedback: boolean
  messageObject: any
  setIsRegenerating?: any
  chatContentRef?: any
  setIsPromptEditing?: (value: boolean) => void
}

// Tailwind-based dropdown toggle component
interface DropdownToggleProps {
  color?: string
  $bgColor?: string
  $isDisabled?: boolean
  $isActive?: boolean
  tag?: string
  className?: string
  disabled?: boolean
  'data-testid'?: string
  'data-tooltip-id'?: string
  children?: React.ReactNode
  onClick?: () => void
}

export const ChatFeedback: React.FC<ChatFeedbackProps> = ({
  showFeedback,
  messageObject,
  setIsRegenerating,
  chatContentRef,
  setIsPromptEditing
}) => {
  const {
    messages,
    rawMessages,
    setMessages,
    currentChat,
    speakingId,
    setSpeakingId,
    selectedMessage,
    setSelectedMessage,
    isStreaming,
    threadType
  } = useContext(ChatbotContext)
  const isBot = messageObject?.role === 'assistant'
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [isPositive, setIsPositive] = useState<boolean | null>(null)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false)
  const [isCopied, setIsCopied] = useState(false)
  const { chatMode } = useContext(ChatbotContext)
  const { selectedAgent, chatModels } = useSelector((state: any) => state.chatbotReducer)
  const { isDarkTheme } = useThemeManager()
  const { onRegenerateResponse } = useChatService()
  const [model, setModel] = useState<any>(null)
  const { speak, voices, stopSpeaking, browserSupportsSpeech } = useSpeech()
  const isDrawerMode = checkIsDrawerMode()

  //@ts-ignore
  const trackCitationOpened = useTrackEvent(appInsights, EventType.IsCitationOpened)
  //@ts-ignore
  const trackSpeakerUsed = useTrackEvent(appInsights, EventType.SpeakerUsed)
  //@ts-ignore
  const trackComparisonMode = useTrackEvent(appInsights, EventType.IsCompareResponseClicked)
  //@ts-ignore
  const trackModelVersionUsed = useTrackEvent(appInsights, EventType.ModelVersionUsed)
  const responseIndex = messageObject?.activeResponseIndex || 0
  const messageResponse = messageObject?.response[responseIndex]
  const content: any = messageResponse.content || ''
  const isNewResponse = Boolean(messageResponse?.runId)
  // Tool-only threads (e.g. Snowflake, per thread.metadata.Type from /api/thread/:id) never get
  // a runId on their responses - allow regenerate for them even though isNewResponse is false,
  // which otherwise exists to hide regenerate on aborted responses (those also lack a runId)
  const isSnowflakeThread = `${threadType || ''}`.toLowerCase() === 'snowflake'
  // A brand-new chat has no thread yet, so threadType can't be known - an externally-sourced
  // Snowflake agent (source: 'Co-Pilot', externalType: 'Snowflake') is known to be tool-only
  // up front from the agent itself, so it covers the 1st response before a thread exists.
  const isExternalSnowflakeAgent = selectedAgent?.externalType === 'Snowflake'
  const isSpeaking = speakingId !== undefined && speakingId === messageObject?.parentId

  useEffect(() => {
    setSpeakingId(undefined)
    stopSpeaking()
  }, [responseIndex])

  const onCopy = () => {
    setIsCopied(true)
    navigator.clipboard.writeText(content)
    setTimeout(() => {
      setIsCopied(false)
    }, 3000)
  }

  const regenerateChatResponse = (model: string) => {
    setIsRegenerating(true)
    trackModelVersionUsed({
      chatType: 'regenerate',
      modelVersion: model
    })
    onRegenerateResponse(
      messageObject.parentId,
      messageObject.response[responseIndex]?.id,
      selectedAgent?.id,
      model
    )
  }

  const navigateResponse = (type: 'increment' | 'decrement') => {
    let responseIndex = messageObject?.activeResponseIndex || 0
    if (type === 'increment') {
      responseIndex = Math.min(responseIndex + 1, (messageObject?.response?.length || 0) - 1)
    } else {
      responseIndex = Math.max(responseIndex - 1, 0)
    }
    const preMessages = messages.slice(
      0,
      messages.findIndex((msg: ChatBubbleItem) => msg.parentId === messageObject?.parentId)
    )
    navigateMsgResponse(
      preMessages,
      rawMessages,
      messageObject?.parentId,
      responseIndex,
      setMessages
    )

    //this ensures citations remain visible when navigating through user prompt responses
    if (messageObject?.role === 'user' && selectedMessage !== null) {
      setSelectedMessage({
        parentId: messageObject?.response[responseIndex]?.id,
        type: 'citations'
      })
    }
  }

  const onRegenerateChange = (selectedModel: any) => {
    setModel(selectedModel)
    regenerateChatResponse(selectedModel?.name)
  }

  const handleSpeaking = async () => {
    if (!browserSupportsSpeech) {
      toast.error(
        'Your browser does not support speech synthesis. Please try on a different browser.'
      )
      return
    }

    // Stop any ongoing speech if a different message is clicked
    if (speakingId !== undefined) {
      stopSpeaking()
    }

    // If the same message is clicked, reset the speakingId and stop
    if (speakingId === messageObject?.parentId) {
      setSpeakingId(undefined)
    } else {
      const voice = getVoice(voices) || voices[0] // Default to the first voice if no specific voice is found
      const lang = 'en-GB' // Set the language to English (UK)
      setSpeakingId(messageObject?.parentId)
      speak(chatContentRef.current?.innerText, {
        voice,
        lang,
        onEnd: () => setSpeakingId(undefined) // Reset speakingId after speech ends
      })
      trackSpeakerUsed({})
    }
  }

  return (
    <div
      className={`min-h-[38px] flex chat-primary transition-opacity duration-200 ${
        !showFeedback ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}>
      <div className={`flex transition-all duration-300 ease-in-out items-center`}>
        {isBot
          ? messageObject?.response[responseIndex]?.modelVersion && (
              <Badge
                variant="light"
                label={getModelDisplayName(
                  chatModels,
                  messageObject?.response[responseIndex]?.modelVersion
                )}
                isDarkMode={isDarkTheme}
                className="p-50 chat-model-badge dsm-badge-brand"
              />
            )
          : isNewResponse && (
              <span
                className={`icon-default rounded-md p-50 transition-all duration-200 ${
                  isStreaming
                    ? 'opacity-40 cursor-not-allowed'
                    : 'cursor-pointer theme-hover-surface'
                }`}>
                <IconPencil
                  data-testid="edit-prompt"
                  size={20}
                  onClick={() =>
                    !isStreaming && setIsPromptEditing ? setIsPromptEditing(true) : null
                  }
                />
              </span>
            )}
        {isBot && messageObject?.response?.length > 1 && (
          <div className={`flex justify-center items-center icon-default`}>
            <span
              className="cursor-pointer icon-default rounded-md theme-hover-surface p-50 ml-1 transition-all duration-200"
              onClick={() => navigateResponse('decrement')}>
              <IconChevronLeft size={16} />
            </span>
            <span className="theme-text-subheading inline-size-max-content text-sm px-[2px]">
              {responseIndex + 1} / {messageObject?.response?.length}
            </span>
            <span
              className="cursor-pointer icon-default rounded-md theme-hover-surface p-50 ml-1 transition-all duration-200"
              data-testid="edit-prompt"
              onClick={() => navigateResponse('increment')}>
              <IconChevronRight size={16} />
            </span>
          </div>
        )}
        {isCopied ? (
          <span className="icon-default ml-2 me-25 rounded-md theme-hover-surface p-50 transition-all duration-200">
            <IconCheck onClick={onCopy} size={18} />
          </span>
        ) : (
          content &&
          content !== '' && (
            <>
              <span
                className={`icon-default cursor-pointer rounded-md theme-hover-surface p-50 transition-all duration-200`}
                data-tooltip-id="copy-chat"
                onClick={onCopy}
                data-testid="copyChat">
                <IconCopy size={18} />
              </span>
              <Tooltip
                id="copy-chat"
                className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                Copy
              </Tooltip>
            </>
          )
        )}
        {isBot && (
          <>
            {(isNewResponse || isSnowflakeThread || isExternalSnowflakeAgent) &&
              !selectedAgent?.isDeleted && (
                <ChatModelDropdown type="regenerate" onDropDownChange={onRegenerateChange} />
              )}
            <span className="cursor-pointer rounded-md theme-hover-surface p-50">
              {isPositive && feedbackSubmitted ? (
                <IconThumbUpFilled
                  className="outline-none"
                  data-testid="like-chat"
                  data-tooltip-id="like-chat"
                  size={20}
                  color="var(--color-primary)"
                  onClick={() => {
                    if (!feedbackSubmitted) {
                      setIsPositive(true)
                      setShowFeedbackModal(true)
                    }
                  }}
                />
              ) : (
                <IconThumbUp
                  className="outline-none"
                  data-testid="like-chat"
                  data-tooltip-id="like-chat"
                  size={20}
                  color="var(--gray-400)"
                  onClick={() => {
                    if (!feedbackSubmitted) {
                      setIsPositive(true)
                      setShowFeedbackModal(true)
                    }
                  }}
                />
              )}
            </span>
            <Tooltip
              id="like-chat"
              className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
              Like
            </Tooltip>
            <span className="cursor-pointer rounded-md theme-hover-surface p-50">
              {!isPositive && feedbackSubmitted ? (
                <IconThumbDownFilled
                  className="outline-none"
                  data-testid="dislike-chat"
                  data-tooltip-id="dislike-chat"
                  size={20}
                  color="var(--color-primary)"
                  onClick={() => {
                    if (!feedbackSubmitted) {
                      setIsPositive(false)
                      setShowFeedbackModal(true)
                    }
                  }}
                />
              ) : (
                <IconThumbDown
                  className="outline-none"
                  data-testid="dislike-chat"
                  data-tooltip-id="dislike-chat"
                  size={20}
                  color="var(--gray-400)"
                  onClick={() => {
                    if (!feedbackSubmitted) {
                      setIsPositive(false)
                      setShowFeedbackModal(true)
                    }
                  }}
                />
              )}
            </span>
            <Tooltip
              id="dislike-chat"
              className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
              Dislike
            </Tooltip>
            {messageResponse.citations && messageResponse.citations.length > 0 && (
              <>
                <div
                  className={`icon-default cursor-pointer chat-primary rounded-md p-50 transition-all duration-200 ${
                    selectedMessage?.parentId === messageObject.parentId &&
                    selectedMessage?.type === 'citations'
                      ? 'theme-icon-accent-active'
                      : 'theme-hover-surface'
                  }`}
                  data-testid="citations-chat"
                  onClick={() => {
                    setSelectedMessage({
                      parentId: messageObject.parentId,
                      type: 'citations'
                    })
                    trackCitationOpened({ mode: chatMode })
                  }}>
                  <IconAlignJustified
                    data-tooltip-id="citations-chat"
                    size={18}
                    color={
                      selectedMessage?.parentId === messageObject.parentId &&
                      selectedMessage?.type === 'citations'
                        ? 'var(--brand-accent-bold)'
                        : 'var(--gray-400)'
                    }
                    className="outline-none"
                  />
                </div>
                <Tooltip
                  id="citations-chat"
                  place="top"
                  className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                  Citations
                </Tooltip>
              </>
            )}
            {!isDrawerMode && messageObject?.response?.length > 1 && (
              <>
                <div
                  className={`border-none icon-default cursor-pointer ml-[0.3rem] chat-primary rounded-md theme-hover-surface p-50 transition-all duration-200`}
                  data-testid="compare-response"
                  data-tooltip-id="compare">
                  <IconLayoutColumns
                    size={20}
                    onClick={() => {
                      trackComparisonMode({})
                      setSelectedMessage({
                        parentId: messageObject.parentId,
                        type: 'compare'
                      })
                    }}
                  />
                </div>
                <Tooltip
                  id="compare"
                  className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                  Side-by-side comparison
                </Tooltip>
              </>
            )}
          </>
        )}
        <>
          <span className="cursor-pointer rounded-md theme-hover-surface p-50 transition-all duration-200">
            <IconVolume2
              data-testid="chat-read"
              data-tooltip-id={`chat-read-${messageResponse?.id}`}
              size={22}
              color={isSpeaking ? 'var(--color-primary)' : 'var(--gray-400)'}
              onClick={handleSpeaking}
            />
          </span>
          {isSpeaking ? (
            <Tooltip
              id={`chat-read-${messageResponse?.id}`}
              className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
              Stop reading
            </Tooltip>
          ) : (
            <Tooltip
              id={`chat-read-${messageResponse?.id}`}
              className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
              Read aloud
            </Tooltip>
          )}
        </>
      </div>
      {!isBot && messageObject?.response?.length > 1 && (
        <div className="flex justify-center items-center icon-default">
          <span
            className="cursor-pointer icon-default rounded-md theme-hover-surface mr-1 p-50 transition-all duration-200"
            data-testid="user-nav-left"
            onClick={() => navigateResponse('decrement')}>
            <IconChevronLeft size={20} />
          </span>
          <span className="theme-text-subheading inline-size-max-content text-sm">
            {responseIndex + 1} / {messageObject?.response?.length}
          </span>
          <span
            className="cursor-pointer icon-default rounded-md theme-hover-surface ml-1 p-50 transition-all duration-200"
            data-testid="user-nav-right"
            onClick={() => navigateResponse('increment')}>
            <IconChevronRight size={20} />
          </span>
        </div>
      )}

      {showFeedbackModal && (
        <ChatFeedbackModal
          open={showFeedbackModal}
          setOpen={setShowFeedbackModal}
          isPositive={isPositive}
          setFeedbackSubmitted={setFeedbackSubmitted}
          messageId={messageResponse?.id}
          threadId={currentChat?.id}
        />
      )}
    </div>
  )
}
